import { ApiError } from '~/_v3/common/actors/errors';
import { apiRequest } from '~/_v3/common/services/httpService';

import type { PublicShare } from '../domain/public-shares';

const baseUrl = import.meta.env.SILAE_EDP_BACKEND_URL;

export type PublicSharingsResponse = {
	paging: {
		limit: number;
		offset: number;
		total: number;
	};
	sharings: PublicShare[];
};

export const loadPublicShares = async (): Promise<PublicSharingsResponse> => {
	return await apiRequest('/api/v1/public/sharings', 'GET', localStorage.getItem('sharingAuthToken'));
};

export const markPublicSharesAsRead = async ({ input: sharingId }) => {
	const response = await fetch(baseUrl + `/api/v1/public/sharings/${sharingId}/marked-as-read`, {
		method: 'POST',
		headers: {
			Accept: 'application/json',
			Authorization: 'Bearer ' + localStorage.getItem('sharingAuthToken')
		}
	});
	return response;
};

export const downloadPublicSharedDocuments = async ({
	input: { documents, sharingId }
}: {
	input: { documents?: string[]; sharingId?: string };
}) => {
	const authToken = localStorage.getItem('sharingAuthToken');
	const response = await fetch(baseUrl + '/api/v1/public/sharings/download', {
		method: 'POST',
		headers: {
			Accept: 'application/octet-stream',
			Authorization: 'Bearer ' + authToken
		},
		body: JSON.stringify({ documents: documents || [], sharingId: sharingId || '' })
	});

	if (!response.ok) {
		const errorBody = await response.json();
		throw new ApiError(`HTTP error! Status: ${response.status}`, errorBody);
	}

	const blob = await response.blob();
	const contentDisposition = response.headers.get('content-disposition');

	return {
		blob,
		contentDisposition
	};
};

export const sendShareCode = async ({ input: mode }) => {
	const response = await fetch(baseUrl + '/api/v1/sharings/send-code', {
		method: 'POST',
		headers: {
			Accept: 'application/json',
			Authorization: 'Bearer ' + localStorage.getItem('sharingCodeToken')
		},
		body: JSON.stringify({ mode })
	});

	if (!response.ok) {
		const errorBody = await response.json();
		throw new ApiError(`HTTP error! Status: ${response.status}`, errorBody);
	}

	return response;
};

export const verifyCode = async ({ input: code }) => {
	const response = await fetch(baseUrl + '/api/v1/sharings/verify-code', {
		method: 'POST',
		headers: {
			Accept: 'application/json',
			Authorization: 'Bearer ' + localStorage.getItem('sharingCodeToken')
		},
		body: JSON.stringify({ code })
	});

	const token: string | null = response.headers.get('Set-Authorization');

	if (!token) {
		const errorBody = await response.json();
		throw new ApiError(`Failed to login: ${response.status}`, errorBody);
	}

	localStorage.setItem('sharingAuthToken', token);
};

export const checkSharingAuthJwt = async () => {
	const token = localStorage.getItem('sharingAuthToken');
	if (!token) {
		const errorBody = { errors: ['jwt_not_found'] };
		throw new ApiError(`Jwt not found`, errorBody);
	}

	// Parse the JWT payload (second part) and check expiration
	let payload = undefined;
	try {
		payload = JSON.parse(atob(token.split('.')[1]));
	} catch (error) {
		const errorBody = { errors: ['invalid_jwt_format'] };
		throw new ApiError(`Invalid JWT format`, errorBody);
	}
	const isExpired = payload.exp * 1000 < Date.now(); // Convert exp to milliseconds

	if (isExpired) {
		const errorBody = { errors: ['jwt_expired'] };
		throw new ApiError(`Jwt has expired`, errorBody);
	}
};

export const checkSharingCodeJwt = async () => {
	const token = localStorage.getItem('sharingCodeToken');
	if (!token) {
		const errorBody = { errors: ['jwt_not_found'] };
		throw new ApiError(`Jwt not found`, errorBody);
	}
	let payload = undefined;
	try {
		payload = JSON.parse(atob(token.split('.')[1]));
	} catch (error) {
		const errorBody = { errors: ['invalid_jwt_format'] };
		throw new ApiError(`Invalid JWT format`, errorBody);
	}
	const isExpired = payload.exp * 1000 < Date.now(); // Convert exp to milliseconds

	if (isExpired) {
		const errorBody = { errors: ['jwt_expired'] };
		throw new ApiError(`Jwt has expired`, errorBody);
	}
};
